import React, { useEffect, useState } from 'react';
import IndexChart from '../components/IndexChart';
import StrategyEditor from '../components/StrategyEditor';
import StrategyStateBadge from '../components/StrategyStateBadge';
import ThemeToggle from '../components/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import { EodLatestResponse, IndexLatestResponse, IndexValuesResponse, Strategy, eodApi, indexApi, strategyApi } from '../services/api';
import DataReferencePage from './DataReferencePage';


function tradingDaysSince(dateStr: string): number {
  const data = new Date(dateStr);
  data.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let count = 0;
  const d = new Date(data);
  d.setDate(d.getDate() + 1);
  while (d <= today) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

function DataFreshnessPill({ date }: { date: string | null }) {
  if (!date) return null;
  const days = tradingDaysSince(date);
  const label = new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const stale = days >= 2;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
      stale
        ? 'dark:bg-amber-500/15 bg-amber-50 dark:text-amber-400 text-amber-600 dark:border-amber-500/25 border border-amber-200'
        : 'dark:bg-green-500/15 bg-green-50 dark:text-green-400 text-green-600 dark:border-green-500/25 border border-green-200'
    }`}>
      {stale ? (
        <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
      ) : (
        <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
      )}
      {label}
    </span>
  );
}

function Navbar({
  fullName,
  onLogout,
  onBack,
  onReference,
  onSwitchToAdmin,
  eodDate,
}: {
  fullName: string;
  onLogout: () => void;
  onBack?: () => void;
  onReference?: () => void;
  onSwitchToAdmin?: () => void;
  eodDate: string | null;
}) {
  return (
    <nav className="shrink-0 relative dark:bg-gsw-navy bg-white border-b dark:border-transparent border-gray-200 px-6 flex justify-between items-center transition-colors duration-200" style={{ paddingTop: '15px', paddingBottom: '12px' }}>
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: 'linear-gradient(90deg, #C9A84C, #E8C97A, #C9A84C)' }} />

      <div className="flex items-center gap-3">
        {onBack && (
          <button
            onClick={onBack}
            className="mr-1 flex items-center gap-1.5 text-sm dark:text-white/60 text-gray-500 dark:hover:text-white hover:text-gsw-navy transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            My Strategies
          </button>
        )}
        <img src="/gsw_logo.png" alt="GSW" className="h-9 object-contain" />
        <span className="text-base font-semibold text-gsw-gold font-playfair">
          GSW Quant Index Platform
        </span>
        <DataFreshnessPill date={eodDate} />
      </div>

      <div className="flex items-center gap-4">
        {onSwitchToAdmin && (
          <button
            onClick={onSwitchToAdmin}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gsw-gold/60 bg-gsw-gold/10 text-gsw-gold hover:bg-gsw-gold/20 transition"
          >
            Switch to Admin View
          </button>
        )}
        {onReference && (
          <button
            onClick={onReference}
            className="flex items-center gap-1.5 text-sm dark:text-white/60 text-gray-500 dark:hover:text-white hover:text-gsw-navy transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Data Docs
          </button>
        )}
        <span className="text-sm dark:text-white/70 text-gray-600">
          {fullName}
          <span className="ml-2 bg-gsw-gold/20 text-gsw-gold-light text-xs font-medium px-2 py-0.5 rounded-full border border-gsw-gold/30">
            Quant
          </span>
        </span>
        <ThemeToggle />
        <button onClick={onLogout} className="text-sm dark:text-gsw-gold-light text-gsw-gold dark:hover:text-white hover:text-gsw-navy transition">
          Logout
        </button>
      </div>
    </nav>
  );
}


function CreateStrategyModal({
  onSubmit,
  onCancel,
}: {
  onSubmit: (name: string, description: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(name.trim(), description.trim());
    } catch {
      setError('Failed to create strategy. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && !submitting && onCancel()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #C9A84C, #E8C97A, #C9A84C)' }} />
        <div className="p-8">
          <h3 className="text-lg font-semibold text-gsw-navy mb-1 font-playfair">New Strategy</h3>
          <p className="text-gray-500 text-sm mb-6">Give your strategy a name to open the editor.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Suggested Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                maxLength={50}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gsw-gold focus:border-transparent transition"
                placeholder="e.g. Momentum Alpha v1"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{name.length} / 50</p>
              <p className="text-xs text-gray-400 mt-1">GoldStandard Wealth will confirm the official name.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={200}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gsw-gold focus:border-transparent resize-none transition"
                placeholder="Brief description of your index methodology"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{description.length} / 200</p>
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !name.trim()}
                className="flex-1 bg-gsw-gold text-gsw-navy rounded-lg py-2.5 text-sm font-semibold hover:bg-gsw-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {submitting ? 'Creating...' : 'Create & Open Editor'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function QuantIndexChartModal({
  strategyId,
  strategyName,
  onClose,
}: {
  strategyId: number;
  strategyName: string;
  onClose: () => void;
}) {
  const [values, setValues] = useState<IndexValuesResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    indexApi.getValues(strategyId)
      .then(({ data }) => setValues(data))
      .catch(() => setError('Failed to load index data.'));
  }, [strategyId]);

  const downloadCsv = () => {
    if (!values) return;
    const today = new Date().toISOString().split('T')[0];
    const filename = `${strategyName.replace(/\s+/g, '_')}_index_${today}.csv`;
    const header = ['Date', 'Index Value', 'Index Daily Return %', 'Portfolio Value', 'Portfolio Daily Return %', 'Portfolio Txn Cost', 'Portfolio Mgmt Fee', 'Rebalance'];
    const dateSet = new Set([...values.index.map((d) => d.date), ...values.portfolio.map((d) => d.date)]);
    const allDates = Array.from(dateSet).sort().reverse();
    const idxByDate = new Map(values.index.map((d) => [d.date, d]));
    const pfByDate = new Map(values.portfolio.map((d) => [d.date, d]));
    const rows = allDates.map((dt) => {
      const idx = idxByDate.get(dt);
      const pf = pfByDate.get(dt);
      return [
        dt,
        idx ? idx.index_value.toFixed(4) : '',
        idx?.daily_return_pct != null ? idx.daily_return_pct.toFixed(3) : '',
        pf ? pf.index_value.toFixed(4) : '',
        pf?.daily_return_pct != null ? pf.daily_return_pct.toFixed(3) : '',
        pf ? pf.txn_cost_drag.toFixed(4) : '',
        pf ? pf.fee_drag.toFixed(4) : '',
        (idx?.rebalance_signal || pf?.rebalance_signal) ? 'Yes' : 'No',
      ];
    });
    const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#0A1628] border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh] overflow-hidden">
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h3 className="text-sm font-semibold text-gray-100">{strategyName}</h3>
            <p className="text-xs text-white/40 mt-0.5">Index performance since inception</p>
          </div>
          <div className="flex items-center gap-3">
            {values && (values.index.length > 0 || values.portfolio.length > 0) && (
              <button
                onClick={downloadCsv}
                className="px-2.5 py-1 text-xs rounded font-medium bg-white/10 text-white border border-white/20 hover:bg-white/20 transition"
                title="Download full series as CSV"
              >
                ↓ CSV
              </button>
            )}
            <button onClick={onClose} className="text-white/40 hover:text-white text-lg leading-none transition">✕</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && <p className="text-sm text-red-400">{error}</p>}
          {!error && values === null && <p className="text-sm text-white/40">Loading…</p>}
          {!error && values !== null && <IndexChart data={values} height={300} />}
        </div>
      </div>
    </div>
  );
}

function PerformancePanel({ strategyId }: { strategyId: number }) {
  const [latest, setLatest] = useState<IndexLatestResponse | null>(null);
  const [chartOpen, setChartOpen] = useState(false);
  const strategyRef = React.useRef<string>('');

  useEffect(() => {
    indexApi.getLatest(strategyId).then(({ data }) => setLatest(data)).catch(() => {});
  }, [strategyId]);

  if (!latest) return null;

  const indexValue = latest.index.index_value;
  const portfolioValue = latest.portfolio.index_value;
  const indexReturn = indexValue != null ? ((indexValue - 1000) / 1000) * 100 : null;
  const portfolioReturn = portfolioValue != null ? ((portfolioValue - 1000) / 1000) * 100 : null;
  const shortfall = latest.implementation_shortfall_pct;
  const asOfDate = latest.portfolio.date ?? latest.index.date;

  if (indexValue == null && portfolioValue == null) return null;

  return (
    <>
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 flex-wrap">
        {indexValue != null && (
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Index</p>
            <p className="text-sm font-bold font-mono" style={{ color: '#C9A84C' }}>{indexValue.toFixed(2)}</p>
            {indexReturn != null && (
              <p className={`text-xs font-semibold ${indexReturn >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {indexReturn >= 0 ? '+' : ''}{indexReturn.toFixed(2)}%
              </p>
            )}
          </div>
        )}
        {portfolioValue != null && (
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Portfolio</p>
            <p className="text-sm font-bold text-gsw-navy font-mono">{portfolioValue.toFixed(2)}</p>
            {portfolioReturn != null && (
              <p className={`text-xs font-semibold ${portfolioReturn >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {portfolioReturn >= 0 ? '+' : ''}{portfolioReturn.toFixed(2)}%
              </p>
            )}
          </div>
        )}
        {shortfall != null && (
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Shortfall</p>
            <p className={`text-sm font-semibold ${shortfall >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {shortfall >= 0 ? '+' : ''}{shortfall.toFixed(2)}%
            </p>
          </div>
        )}
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">As of</p>
          <p className="text-xs text-gray-500">{asOfDate ? fmtDate(asOfDate) : '—'}</p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setChartOpen(true); }}
          className="ml-auto text-xs px-2.5 py-1 rounded border border-gsw-gold/50 text-gsw-gold hover:bg-gsw-gold/10 transition"
        >
          View Chart
        </button>
      </div>
      {chartOpen && (
        <QuantIndexChartModal
          strategyId={strategyId}
          strategyName={strategyRef.current}
          onClose={() => setChartOpen(false)}
        />
      )}
    </>
  );
}

export default function QuantDashboard({ onSwitchToAdmin }: { onSwitchToAdmin?: () => void }) {
  const { user, logout } = useAuth();

  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [allowMultiple, setAllowMultiple] = useState(true);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReference, setShowReference] = useState(false);

  const [eodData, setEodData] = useState<EodLatestResponse | null>(null);

  useEffect(() => {
    Promise.all([
      strategyApi.getAll(),
      strategyApi.getConfig(),
      eodApi.getLatest().catch(() => ({ data: null })),
    ])
      .then(([{ data: strats }, { data: cfg }, { data: eod }]) => {
        setStrategies(strats);
        setAllowMultiple(cfg.allow_multiple_strategies);
        if (eod) setEodData(eod);
      })
      .catch(() => setFetchError('Failed to load strategies.'))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (name: string, description: string) => {
    const { data } = await strategyApi.create({ suggested_name: name, description: description || undefined });
    setStrategies((prev) => [data, ...prev]);
    setShowCreateModal(false);
    setSelectedStrategy(data);
  };

  const handleStrategyUpdate = (updated: Strategy) => {
    setSelectedStrategy(updated);
    setStrategies((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const canCreateNew = allowMultiple || strategies.length === 0;

  // ── Reference view ───────────────────────────────────────────────────────────
  if (showReference) {
    return (
      <DataReferencePage
        fullName={user?.full_name ?? ''}
        backLabel={selectedStrategy ? 'Back to Editor' : 'My Strategies'}
        onBack={() => setShowReference(false)}
        onLogout={logout}
      />
    );
  }

  // ── Editor view ─────────────────────────────────────────────────────────────
  if (selectedStrategy) {
    return (
      <div className="h-screen flex flex-col overflow-hidden dark:bg-gray-900 bg-gray-50 transition-colors duration-200">
        <Navbar
          fullName={user?.full_name ?? ''}
          onLogout={logout}
          onBack={() => setSelectedStrategy(null)}
          onReference={() => setShowReference(true)}
          onSwitchToAdmin={onSwitchToAdmin}
          eodDate={eodData?.date ?? null}
        />
        <StrategyEditor
          strategy={selectedStrategy}
          onUpdate={handleStrategyUpdate}
          eodData={eodData}
        />
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen dark:bg-gray-900 bg-gray-50 flex flex-col transition-colors duration-200">
      <Navbar fullName={user?.full_name ?? ''} onLogout={logout} onReference={() => setShowReference(true)} onSwitchToAdmin={onSwitchToAdmin} eodDate={eodData?.date ?? null} />

      <main className="flex-1 p-6 max-w-3xl mx-auto w-full">
        {loading && (
          <p className="text-gray-400 text-sm mt-12 text-center">Loading...</p>
        )}

        {fetchError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-5 py-4 mt-8">
            {fetchError}
          </div>
        )}

        {!loading && !fetchError && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #C9A84C, #E8C97A, #C9A84C)' }} />

            {/* Header */}
            <div className="px-6 py-5 flex items-center justify-between border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gsw-navy font-playfair">My Strategies</h2>
              {canCreateNew && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-1.5 bg-gsw-gold text-gsw-navy text-sm font-semibold px-4 py-2 rounded-lg hover:bg-gsw-gold-light transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Strategy
                </button>
              )}
            </div>

            {/* Strategy list */}
            {strategies.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <p className="text-gray-400 text-sm mb-4">You haven't created a strategy yet.</p>
                {canCreateNew && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-gsw-gold text-gsw-navy text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-gsw-gold-light transition"
                  >
                    Create Your First Strategy
                  </button>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {strategies.map((s) => (
                  <li key={s.id} className="px-6 py-4">
                    <button
                      className="w-full text-left hover:bg-gray-50 transition flex items-center justify-between gap-4 -mx-6 px-6 py-0"
                      onClick={() => setSelectedStrategy(s)}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                        {s.description && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">{s.description}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Created {fmtDate(s.created_at)}</p>
                      </div>
                      <div className="shrink-0 flex items-center gap-3">
                        <StrategyStateBadge state={s.state} />
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                    {s.state === 'LIVE' && <PerformancePanel strategyId={s.id} />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </main>

      {showCreateModal && (
        <CreateStrategyModal
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
}
